import {
  COMMUNICATION_OPTIONS,
  SENSORY_OPTIONS,
  TRIGGER_OPTIONS,
  splitCareFocusSelection,
} from "../../../constants/childProfileOptions";
import {
  CARE_SETTING_OPTIONS,
  TRACKING_FOCUS_OPTIONS,
} from "../../../constants/careContextOptions";
import { SUPPORT_PROFILE_OPTIONS } from "./supportProfileOptions";

const HOSPICE_SUPPORT_PROFILE_OPTIONS = {
  ...SUPPORT_PROFILE_OPTIONS,
  favoriteActivities: [
    "Music",
    "Family photos",
    "Faith or prayer",
    "Quiet conversation",
    "Favorite shows",
    "Sitting outside",
    "Hand massage",
    "Reading aloud",
    "Memory stories",
  ],
  interests: [
    "Family stories",
    "Music",
    "Faith or community",
    "Nature",
    "Favorite shows",
    "Photos",
    "Pets",
    "Sports",
    "Cooking memories",
  ],
  comfortItems: [
    "Blanket",
    "Photos",
    "Favorite pillow",
    "Warm drink",
    "Lip balm",
    "Music",
    "Prayer beads or faith item",
    "Soft clothing",
  ],
  favoriteFoods: [
    "Soup",
    "Yogurt",
    "Smoothies",
    "Tea",
    "Ice chips",
    "Favorite snacks",
    "Soft foods",
    "Small sips",
    "Comfort foods",
  ],
  whatHelps: [
    "Repositioning",
    "Quiet room",
    "Dim lights",
    "Music",
    "Warm blanket",
    "Mouth care",
    "Small sips",
    "Gentle reassurance",
    "Pain plan",
    "Family nearby",
  ],
  whatCanBeDifficult: [
    "Pain",
    "Breathing changes",
    "Restlessness",
    "Too many visitors",
    "Being rushed",
    "Noise",
    "Bright lights",
    "Moving positions",
    "Nausea",
    "Fatigue",
    "Confusion",
  ],
  earlyWarningSigns: [
    "More pain",
    "Restlessness",
    "Breathing changes",
    "Less alert",
    "New confusion",
    "Not eating or drinking",
    "Skin color changes",
    "Cool hands or feet",
    "More anxiety",
  ],
  thingsThatUsuallyHelp: [
    "Repositioning",
    "Mouth care",
    "Pain medication plan",
    "Quiet room",
    "Music",
    "Warm blanket",
    "Gentle touch if welcomed",
    "Reduce visitors",
    "Call hospice nurse",
  ],
  thingsToAvoid: [
    "Rushing care",
    "Forcing food or drink",
    "Too many visitors",
    "Arguing or correcting",
    "Bright lights",
    "Loud noise",
    "Moving without explaining",
    "Talking over them",
  ],
  safetyConsiderations: [
    "Fall risk",
    "Choking or swallowing risk",
    "Oxygen safety",
    "Medication safety",
    "Skin breakdown risk",
    "Do not leave alone during transfers",
    "Call hospice nurse first",
    "None",
  ],
  communicationStyle: [
    "Speak gently",
    "Use short updates",
    "Explain before touching or moving",
    "Allow quiet",
    "Give time to respond",
    "Family helps interpret needs",
    "Use familiar words",
    "Hearing support",
    "Vision support",
  ],
  familySupportTips: [
    "Keep the room calm",
    "Share changes with the care circle",
    "Offer small sips if welcomed",
    "Avoid forcing food or drink",
    "Ask before touching",
    "Keep comfort items nearby",
    "Write questions for the nurse",
    "Make space for rest",
  ],
  sensorySupports: [
    "Quiet room",
    "Dim lights",
    "Soft clothing",
    "Warm blanket",
    "Gentle touch if welcomed",
    "Familiar voices",
    "Music",
    "Mouth care",
    "Repositioning",
  ],
  transitionChallenges: [
    "Moving positions",
    "Transfers",
    "Bathing or personal care",
    "Medication changes",
    "Visitor changes",
    "Care team visits",
    "Leaving home",
    "Nighttime settling",
  ],
  transitionSupports: [
    "Explain before moving",
    "Move slowly",
    "Pause for rest",
    "Use familiar caregiver",
    "Keep comfort item nearby",
    "Prepare supplies first",
    "Limit visitors",
    "Follow hospice plan",
  ],
  memoryChallenges: [
    "New confusion",
    "Less alert",
    "Harder to follow conversation",
    "Does not recognize people",
    "Anxiety or restlessness",
    "More withdrawn",
    "Repeating questions",
  ],
  memoryAnchors: [
    "Family photos",
    "Familiar voices",
    "Favorite music",
    "Faith or prayer",
    "Simple reassurance",
    "Quiet presence",
    "Short reminders",
    "Comfort object",
  ],
  mobilitySupports: [
    "Repositioning",
    "Transfer help",
    "Fall precautions",
    "Skin checks",
    "Pressure relief",
    "Slow movement",
    "Clear pathway",
    "Call hospice nurse",
  ],
  symptomPatterns: [
    "Pain",
    "Breathing changes",
    "Restlessness",
    "Anxiety",
    "Nausea",
    "Appetite change",
    "Bathroom change",
    "Skin changes",
    "Less alert",
  ],
  sleepRestPatterns: [
    "Sleeping more than usual",
    "Restless sleep",
    "Dozing on and off",
    "Awake at night",
    "Pain affecting rest",
    "Breathing affecting rest",
    "Needs repositioning",
    "Anxiety or restlessness",
  ],
  sleepRestSupports: [
    "Repositioning",
    "Pain plan",
    "Quiet room",
    "Dim lights",
    "Music",
    "Warm blanket",
    "Mouth care",
    "Family nearby",
    "Call hospice nurse",
  ],
};

const HOSPICE_PROFILE_OPTIONS = {
  supportProfile: HOSPICE_SUPPORT_PROFILE_OPTIONS,
  communication: [
    "Speak gently",
    "Use short updates",
    "Explain before touching or moving",
    "Give time to respond",
    "Family helps interpret needs",
    "Hearing support",
    "Vision support",
    "Language interpretation",
  ],
  sensory: [
    "Noise sensitivity",
    "Light sensitivity",
    "Touch sensitivity",
    "Temperature comfort",
    "Positioning comfort",
    "Mouth comfort",
    "Breathing comfort",
    "Needs quiet space",
    "Prefers familiar voices",
  ],
  triggers: [
    "Medication timing",
    "Positioning",
    "Pain",
    "Breathing",
    "Bathroom needs",
    "Food or drink",
    "Anxiety",
    "Too many visitors",
    "Noise",
    "Bright lights",
    "Not sure",
  ],
  trackingFocus: [
    { code: "COMFORT", label: "Comfort" },
    { code: "PAIN", label: "Pain" },
    { code: "MEDICATION", label: "Medication" },
    { code: "SLEEP", label: "Rest / sleep" },
    { code: "FOOD_APPETITE", label: "Food / drink" },
    { code: "BREATHING", label: "Breathing" },
    { code: "MOOD", label: "Mood / anxiety" },
    { code: "TOILETING_HYGIENE", label: "Bathroom / hygiene" },
    { code: "MOBILITY", label: "Mobility / positioning" },
    { code: "APPOINTMENTS", label: "Care team visits" },
  ],
  careSetting: [
    { code: "HOME", label: "Home" },
    { code: "HOSPICE", label: "Hospice / palliative care" },
    { code: "MEDICAL", label: "Medical care" },
    { code: "RESIDENTIAL", label: "Residential care" },
    { code: "SHARED_HOUSEHOLDS", label: "Shared households" },
    { code: "TRAVEL", label: "Travel / outings" },
  ],
};

const getPrimaryCareFocusCode = (selectedConditions = []) => {
  const { primaryFocus } = splitCareFocusSelection(selectedConditions);
  return String(primaryFocus?.code || "").toUpperCase();
};

export const getProfileLensOptions = (selectedConditions = []) => {
  if (getPrimaryCareFocusCode(selectedConditions) === "HOSPICE") {
    return HOSPICE_PROFILE_OPTIONS;
  }

  return {
    supportProfile: SUPPORT_PROFILE_OPTIONS,
    communication: COMMUNICATION_OPTIONS,
    sensory: SENSORY_OPTIONS,
    triggers: TRIGGER_OPTIONS,
    trackingFocus: TRACKING_FOCUS_OPTIONS,
    careSetting: CARE_SETTING_OPTIONS,
    sleepRestPatterns: [
      "Difficulty falling asleep",
      "Frequent night waking",
      "Early morning waking",
      "Restless sleep",
      "Needs specific routine",
      "Sensory needs for sleep",
      "Nightmares or night terrors",
      "Sleep walking",
      "Daytime fatigue",
    ],
    sleepRestSupports: [
      "Consistent bedtime routine",
      "Quiet room",
      "Dim lights",
      "White noise",
      "Comfort item",
      "Visual schedule",
      "Movement before bed",
      "Medication plan",
      "Caregiver check-in",
    ],
    sensorySupports: [
      "Headphones",
      "Quiet room",
      "Movement break",
      "Weighted blanket",
      "Fidget or comfort item",
      "Dim lights",
      "White noise",
      "Deep pressure if welcomed",
      "Visual schedule",
    ],
    transitionChallenges: [
      "Stopping preferred activity",
      "Leaving home",
      "Starting bedtime",
      "Changing rooms",
      "Waiting",
      "Unexpected changes",
      "New people",
      "Busy places",
      "Appointments",
    ],
    transitionSupports: [
      "Visual timer",
      "First-Then language",
      "Preview the plan",
      "Offer choices",
      "Transition object",
      "Countdown",
      "Extra processing time",
      "Movement break",
      "Written schedule",
    ],
    memoryChallenges: [
      "Forgets recent events",
      "Repeats questions",
      "Gets lost or disoriented",
      "Harder evenings",
      "New places feel confusing",
      "Misses familiar routines",
      "Needs reminders",
    ],
    memoryAnchors: [
      "Photos",
      "Written reminders",
      "Calendar",
      "Familiar routine",
      "Simple choices",
      "Labels or signs",
      "Favorite music",
      "Known caregiver",
    ],
    mobilitySupports: [
      "Fall precautions",
      "Clear pathway",
      "Transfer help",
      "Mobility device",
      "Rest breaks",
      "Slow transitions",
      "Supervision on stairs",
      "Comfortable shoes",
    ],
    symptomPatterns: [
      "Pain",
      "Fatigue",
      "Sleep changes",
      "Appetite changes",
      "Bathroom changes",
      "Mood changes",
      "Medication side effects",
      "Seizure signs",
      "Breathing changes",
    ],
  };
};
